import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import {
	Button,
	Card,
	CardBody,
	CardText,
	Modal,
	ModalBody,
	ModalHeader,
} from 'reactstrap';

import {
	DangerAlert,
	Loading,
	OAUTH_PROVIDERS,
	OAUTH_TYPES,
} from '@techchange/components';
import { messages } from '../../i18n';

import { buildApiURL } from '../utils';

import './ApiIntegrationRequired.scss';

const getLocalStorageKey = (integration) => `oauth-flow-${integration.id}:status`;

const OAUTH_FLOW_IN_PROGRESS = 'in-progress';
const OAUTH_FLOW_AUTHORIZED = 'authorized';

const propTypes = {

	/**
	 * The specific API integration (if any) to require.
	 */
	requiredIntegration: PropTypes.shape({

		/**
		 * The integration ID.
		 */
		id: PropTypes.string.isRequired,

		/**
		 * The integration name.
		 */
		name: PropTypes.string.isRequired,

		/**
		 * The integration logo.
		 */
		logo: PropTypes.string,

		/**
		 * The integration type.
		 */
		type: PropTypes.oneOf([OAUTH_TYPES.API]).isRequired,

		/**
		 * The integration provider.
		 */
		provider: PropTypes.oneOf([
			OAUTH_PROVIDERS.MIRO,
			OAUTH_PROVIDERS.ZOOM,
		]).isRequired,

		/**
		 * The access token data for the current user, which indicates
		 * an active connection to the API integration.
		 */
		oauth_integration_tokens: PropTypes.arrayOf(PropTypes.shape({

			/**
			 * The access token ID.
			 */
			id: PropTypes.string.isRequired,

		})).isRequired,

	}),

	/**
	 * The data, state, and callback functions related to
	 * all active API integrations for the current scope.
	 */
	apiIntegrations: PropTypes.shape({

		/**
		 * True, if loading the API integrations data.
		 */
		loading: PropTypes.bool.isRequired,

		/**
		 * The error (if any) related to loading the API integrations data.
		 */
		error: PropTypes.bool.isRequired,

		/**
		 * Callback function to refresh the API integrations data.
		 */
		refresh: PropTypes.func.isRequired,

	}).isRequired,

	/**
	 * True, if the current user is required to authorize a
	 * connection to the specific API integration.
	 */
	isConnectionRequired: PropTypes.bool.isRequired,

	/**
	 * True, if the child content to render should be hidden
	 * until the user is connected to the specific API integration.
	 */
	hideContentUntilConnected: PropTypes.bool.isRequired,

	/**
	 * The child content to render.
	 */
	children: PropTypes.oneOfType([
		PropTypes.element,
		PropTypes.node,
		PropTypes.func,
	]).isRequired,

	/**
	 * Optional render function to use in place of
	 * JSX children.
	 */
	render: PropTypes.func,

	/**
	 * Callback function to handle the current user
	 * choosing not to connect to the required API integration.
	 */
	onCancel: PropTypes.func.isRequired,

	/**
	 * The function used to make API requests through the TechChange oAuth API.
	 */
	onUseAPI: PropTypes.func.isRequired,

};

const defaultProps = {
	isConnectionRequired: true,
	hideContentUntilConnected: false,
};

/**
 * Shared component to ensure that the current user is connected
 * to a specified API integration in order to access the child content.
 *
 * @param {Object} props The props.
 * @returns {Object} The rendered component.
 */
const ApiIntegrationRequired = (props) => {

	const {
		requiredIntegration,
		apiIntegrations,
		isConnectionRequired,
		hideContentUntilConnected,
		children,
		onCancel,
		onUseAPI,
		...restProps
	} = props;

	const intl = useIntl();

	// Keep track of the status of the authorization flow
	// i.e. a reference to the browser tab for the current request
	const [activeAuthTab, setActiveAuthTab] = useState(null);

	// Keep track of the error (if any) related to the authorization flow
	const [showAuthError, setShowAuthError] = useState(false);

	// Keep track of the account info (if any) related to the
	// current user's connection to the API integration
	const [currentAccountInfo, setCurrentAccountInfo] = useState({
		error: false,
		data: null,
	});

	// Check if the current user has an active connection with
	// the required API integration
	const isConnected =
		Boolean(requiredIntegration?.oauth_integration_tokens?.length === 1);

	/**
	 * Handles starting a new authorization request for the API integration.
	 *
	 * @param {object} integration  The API integration.
	 */
	const handleAuthorizationRequest = (integration) => {

		// Build the redirection URL to start the authorization flow
		const href = buildApiURL(integration, {
			opened_in_new_tab: true,
		});

		// Store the "in-progress" state for the authorization flow in local storage
		// NOTE: We listen for an update in useEffect below to wait for successful authorization
		window.localStorage.setItem(
			getLocalStorageKey(integration),
			OAUTH_FLOW_IN_PROGRESS,
		);

		// Open the redirection URL in a new browser tab
		const newTab = window.open(
			href,
			'ApiAuthorizationWindow',
			'popup=true',
		);

		// Reset the error state
		setShowAuthError(false);

		// Keep track of a reference to the new browser tab
		setActiveAuthTab(newTab);

	};

	// Listen for connection to successfully authorize in the new browser tab...
	useEffect(() => {

		if (activeAuthTab &&
			!isConnected &&
			isConnectionRequired &&
			requiredIntegration &&
			apiIntegrations) {

			const listener = async (event) => {

				if (event &&
					event.key === getLocalStorageKey(requiredIntegration)) {

					// Check if successfully authorized the connection...
					if (event.newValue === OAUTH_FLOW_AUTHORIZED) {

						// Refresh API integrations data for current user
						await apiIntegrations.refresh();

					// Otherwise, check for an error...
					} else {

						console.error('Error authorizing the API integration...');
						console.error(event.newValue);

						setShowAuthError(true);

					}

					// Close the browser tab
					activeAuthTab.close();

					// Remove the state for the authorization flow in local storage
					window.localStorage.removeItem(
						getLocalStorageKey(requiredIntegration)
					);

				}

			};

			window.addEventListener('storage', listener);

			return () => window.removeEventListener('storage', listener);

		}

		return () => {};

	}, [
		activeAuthTab,
		isConnected,
		isConnectionRequired,
		requiredIntegration,
		apiIntegrations,
	]);

	// Once connected, fetch the account/token data for the current user...
	useEffect(() => {

		if (isConnected && requiredIntegration) {

			let requestParams;

			if (requiredIntegration.provider === OAUTH_PROVIDERS.MIRO) {

				/**
				 * The Miro account info for the current user.
				 *
				 * See the exact API response here:
				 * https://developers.miro.com/reference/get-access-token-context
				 */

				requestParams = {
					url: '/v1/oauth-token',
					method: 'get',
				};

			} else if (requiredIntegration.provider === OAUTH_PROVIDERS.ZOOM) {

				/**
				 * The Zoom account info for the current user.
				 *
				 * See the exact API response here:
				 * https://developers.zoom.us/docs/api/rest/reference/zoom-api/methods/#operation/user
				 */

				requestParams = {
					url: '/v2/users/me',
					method: 'get',
				};

			} else {
				console.warn(
					`The API integration provider=${requiredIntegration.provider} is not supported.`
				);
			}

			if (requestParams) {

				onUseAPI(requiredIntegration, requestParams).then((response) => {

					if (response && response.data) {

						setCurrentAccountInfo({
							error: false,
							data: response.data,
						});

					}

				}).catch((error) => {

					console.error(error);
					setCurrentAccountInfo({
						error: true,
						data: null,
					});

				});

			}

		}

	}, [
		isConnected,
		requiredIntegration,
		onUseAPI,
	]);

	// Determine the logo for the specified API integration
	let logo;

	if (requiredIntegration && requiredIntegration.logo) {

		logo = (
			<img
				src={requiredIntegration.logo}
				alt="" />
		);

	} else {

		logo = (
			<i
				className="fas fa-plug fa-2x"
				aria-hidden="true">
			</i>
		);

	}

	// Determine the text description for the specified API integration
	let body;

	if (requiredIntegration) {

		switch (requiredIntegration.provider) {

		case OAUTH_PROVIDERS.MIRO:

			body = intl.formatMessage(
				messages.TC_COMPONENTS_API_INTEGRATION_REQUIRED_AUTHORIZE_MODAL_MIRO_SHORT_DESCRIPTION
			);
			break;

		case OAUTH_PROVIDERS.ZOOM:

			body = intl.formatMessage(
				messages.TC_COMPONENTS_API_INTEGRATION_REQUIRED_AUTHORIZE_MODAL_ZOOM_SHORT_DESCRIPTION
			);
			break;

		default :
			body = '';
		}

	}

	// Determine the child content to render
	let childContent = null;

	if (!isConnectionRequired ||
		isConnected ||
		!hideContentUntilConnected) {

		if (props.render) {

			childContent = props.render({
				apiIntegrationAccountInfo: {
					connected: isConnected,
					error: currentAccountInfo.error,
					data: structuredClone(currentAccountInfo.data),
				},
			});

		} else {

			childContent = React.Children.map(
				children,
				(child) => React.cloneElement(child, {
					apiIntegrationAccountInfo: {
						connected: isConnected,
						error: currentAccountInfo.error,
						data: structuredClone(currentAccountInfo.data),
					},
				}),
			);

		}

	}

	// If not connected, prompt the user to authorize connection
	// with specified API integration via the modal
	return (
		<>
			{childContent}
			<Modal
				{...restProps}
				className="tc-api-integration-required-authorize-modal"
				size="lg"
				isOpen={isConnectionRequired && !isConnected}
				toggle={onCancel}>
				<ModalHeader
					tag="h2"
					toggle={onCancel}>
					<FormattedMessage
						{...messages.TC_COMPONENTS_API_INTEGRATION_REQUIRED_AUTHORIZE_MODAL_TITLE_TEXT} />
				</ModalHeader>
				<ModalBody>
					{apiIntegrations.error &&
						<DangerAlert dismissible={false}>
							<FormattedMessage
								{...messages.TC_COMPONENTS_API_INTEGRATION_REQUIRED_AUTHORIZE_MODAL_ERROR_TEXT} />
						</DangerAlert>
					}
					<Loading isLoading={apiIntegrations.loading}>
						{!requiredIntegration &&
							!apiIntegrations.error &&
								<DangerAlert dismissible={false}>
									<FormattedMessage
										{...messages.TC_COMPONENTS_API_INTEGRATION_REQUIRED_AUTHORIZE_MODAL_DISABLED_TEXT} />
								</DangerAlert>
						}
						{showAuthError &&
							requiredIntegration &&
								<DangerAlert dismissible={false}>
									<FormattedMessage
										{...messages.TC_COMPONENTS_API_INTEGRATION_REQUIRED_AUTHORIZE_MODAL_FAILURE_TEXT}
										values={{ name: requiredIntegration.name }} />
								</DangerAlert>
						}
						{requiredIntegration &&
							<Card className="tc-api-integration-required-authorize-modal-card">
								<CardBody className="d-flex align-items-center">
									{logo}
									<div className="tc-api-integration-required-authorize-modal-card-text">
										<div className="font-weight-bold">
											{requiredIntegration.name}
										</div>
										<CardText className="card-text">
											{body}
										</CardText>
									</div>
									<Button
										color="primary"
										aria-label={intl.formatMessage(messages.TC_COMPONENTS_API_INTEGRATION_REQUIRED_AUTHORIZE_MODAL_CONNECT_BUTTON_ARIA_LABEL, {
											name: requiredIntegration.name
										})}
										onClick={() => handleAuthorizationRequest(requiredIntegration)}>
										<FormattedMessage
											{...messages.TC_COMPONENTS_API_INTEGRATION_REQUIRED_AUTHORIZE_MODAL_CONNECT_BUTTON_TEXT} />
									</Button>
								</CardBody>
							</Card>
						}
					</Loading>
				</ModalBody>
			</Modal>
		</>
	);

};

ApiIntegrationRequired.propTypes = propTypes;
ApiIntegrationRequired.defaultProps = defaultProps;

export default ApiIntegrationRequired;
